// Push-note / write-back api fns — carved verbatim from `src/api/scribeAPIV2.ts`
// (which now re-exports them) as D1 of the `integrations/ehr` family
// (ARCFE-1087).
//
// TWO PAIRS SHARE A ROUTE and neither pair is a duplicate to be collapsed:
//   - `syncNotesToEHR` and `syncSectionsNoteToEHR` both POST
//     `/ehr/uploader/{sessionId}/notes`. The second builds a four-key sectional
//     payload from an editor HTML map, and posts with NO body when that map is
//     absent — a distinct request, not a wrapper.
//   - `syncNoteToIntegration` and `syncSectionalNoteToIntegration` differ only
//     in the route tail (`/notes` vs `/sectional-notes`).
// Both were transcribed rather than derived, for that reason.
import type {
  GetUploadFileOptionsResponse,
  SectionalWriteBackSessionsHTML,
  SyncConsultNoteEhrLaunchResponse,
  SyncConsultNoteToEhrLaunchRequest,
  SyncNotesToEHRResponse,
  SyncNotesWithOptionRequest,
  SyncNotesWithOptionsResponse,
  SyncNoteToEHRRequest,
  SyncNoteToIntegrationRequest,
  SyncNoteToIntegrationResponse,
  SyncSDEsToEHRRequest,
  SyncSectionalNoteToIntegrationRequest,
} from '../../contracts/types';
import { http } from './ehrHttp';

export const syncNotesToEHR = (
  sessionId: string,
  data: SyncNoteToEHRRequest | undefined
) => {
  return http().post<SyncNotesToEHRResponse>(`/ehr/uploader/${sessionId}/notes`, {
    ...data,
  });
};

export const syncSDEsToEHR = (
  sessionId: string,
  data: SyncSDEsToEHRRequest | undefined
) => {
  return http().post<SyncNotesToEHRResponse>(
    `/ehr-launch/sessions/${sessionId}/consult-note/sectional`,
    {
      ...data,
    }
  );
};

export const syncNotesToEHRWithEHRLaunch = (
  params: {
    sessionId: string;
    profile_name?: string | null;
  } & SyncConsultNoteToEhrLaunchRequest
) => {
  return http().post<SyncConsultNoteEhrLaunchResponse>(
    `/ehr-launch/sessions/${params.sessionId}/consult-note?profile_name=${params.profile_name}`,
    params
  );
};

export const syncSectionsNoteToEHR = ({
  sessionId,
  sectionsInHTML,
}: {
  sessionId: string;
  sectionsInHTML?: SectionalWriteBackSessionsHTML;
}) => {
  if (!sectionsInHTML) {
    return http().post<SyncNotesToEHRResponse>(`/ehr/uploader/${sessionId}/notes`);
  }
  const payload: SyncNoteToEHRRequest = {
    assessment_note: sectionsInHTML['assessment_note'],
    hpi_note: sectionsInHTML['hpi_note'],
    physical_exam_note: sectionsInHTML['physical_exam_note'],
    review_of_systems_note: sectionsInHTML['review_of_systems_note'],
  };
  return http().post<SyncNotesToEHRResponse>(`/ehr/uploader/${sessionId}/notes`, payload);
};

export const syncNoteToIntegration = ({
  request,
  sessionId,
}: {
  request?: SyncNoteToIntegrationRequest;
  sessionId: string;
}) => {
  return http().post<SyncNoteToIntegrationResponse>(
    `/integrations/session/${sessionId}/notes`,
    request
  );
};

export const syncSectionalNoteToIntegration = ({
  request,
  sessionId,
}: {
  request?: SyncSectionalNoteToIntegrationRequest;
  sessionId: string;
}) => {
  return http().post<SyncNoteToIntegrationResponse>(
    `/integrations/session/${sessionId}/sectional-notes`,
    request
  );
};

export const syncNoteWithOptionsToIntegration = ({
  request,
  sessionId,
}: {
  request: SyncNotesWithOptionRequest;
  sessionId: string;
}) => {
  return http().post<SyncNotesWithOptionsResponse>(
    `/integrations/session/${sessionId}/notes-with-option`,
    request
  );
};

/**
 * The one endpoint on this door with no hook over it: its sole call site is a
 * direct `await` inside `src/features/note-generation/stream-response/legacy/
 * _stream-response.ts`, so no hook-shaped carve will ever carry it along.
 */
export const syncNoteAudit = ({
  request,
  sessionId,
}: {
  request?: SyncNoteToIntegrationRequest;
  sessionId: string;
}) => {
  return http().post(`/integrations/${sessionId}/notes/audit`, request);
};

export const uploadFileToIntegration = ({
  sessionId,
  documentId,
  optionId,
}: {
  sessionId: string;
  documentId?: string;
  optionId?: string;
}) => {
  const url = `/integrations/session/${sessionId}/file`;
  const body: { document_id?: string; option_id?: string } = {};
  if (documentId !== undefined) body.document_id = documentId;
  if (optionId !== undefined) body.option_id = optionId;
  return Object.keys(body).length > 0
    ? http().post<SyncNoteToIntegrationResponse>(url, body)
    : http().post<SyncNoteToIntegrationResponse>(url);
};

const GET_UPLOAD_FILE_OPTIONS_TIMEOUT_MS = 10_000;

/**
 * `GET /integrations/session/{sessionId}/file/options`. Backend endpoint not
 * shipped yet. Mirrors `getSyncDocumentOptions`: empty `options` means the
 * client can upload directly; non-empty means the client must show a picker
 * and pass the selected `option_id` back to `uploadFileToIntegration`.
 */
export const getUploadFileOptions = ({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId?: string;
}) => {
  return http().get<GetUploadFileOptionsResponse>(
    `/integrations/session/${sessionId}/file/options`,
    { params: { document_id: documentId }, timeout: GET_UPLOAD_FILE_OPTIONS_TIMEOUT_MS }
  );
};
